import { PAYMENT_DIRECTION_ID, PAYOUT_DIRECTION_ID } from 'constants/directions';

import { PresetsItemsSection } from 'pages/Presets/components/PresetsItemsSection/PresetsItemsSection';
import { useGatewayCurrencies } from 'pages/Presets/hooks/useGatewayCurrencies';
import { usePresetsErrors } from 'pages/Presets/hooks/usePresetsErrors';
import { HttpError } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Typography } from 'shared/mui/Typography';
import { AutocompleteInput } from 'shared/react-admin/Inputs';
import { TextInput } from 'shared/react-admin/Inputs/TextInputs/TextInput/TextInput';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { cleanEmpty } from 'utils/cleanEmpty';

import { GeneralInfoBoxStyled } from './PresetsCreateForm.styled';

export type PresetItemFormValue = {
  id?: string;
  direction: string;
  amount: string;
  badge: string | null;
  description: string | null;
};

export type PresetsCreateFormValues = {
  merchant: string | null;
  gateway: string | null;
  currency: string | null;
  paymentComment: string | null;
  paymentItems: PresetItemFormValue[];
  payoutItems: PresetItemFormValue[];
};

type Props = {
  error?: HttpError | null;
};

export const PresetsCreateForm: React.FC<Props> = ({ error = null }) => {
  const { watch, resetField } = useFormContext<PresetsCreateFormValues>();
  const [merchantWatched, gatewayWatched, paymentItems, payoutItems] = watch([
    'merchant',
    'gateway',
    'paymentItems',
    'payoutItems',
  ]);
  const { gateway, currencyIds } = useGatewayCurrencies(gatewayWatched);

  const hasNoPresets = !paymentItems?.length && !payoutItems?.length;

  usePresetsErrors(error);

  return (
    <Box sx={{ pt: '24px' }}>
      <Typography fontWeight={500} sx={{ mb: '16px' }} variant="body1">
        Общая информация
      </Typography>
      <GeneralInfoBoxStyled>
        <ReferenceInput
          filter={{ archive: false }}
          perPage={200}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            isRequired
            label="Мерчант"
            onChange={() => {
              resetField('gateway', { defaultValue: null });
              resetField('currency', { defaultValue: null });
            }}
            optionText="name"
            optionValue="@id"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput
          filter={cleanEmpty({
            'merchantAccounts.merchants': merchantWatched || undefined,
          })}
          perPage={200}
          reference="gateways"
          source="gateway"
        >
          <AutocompleteInput
            disabled={!merchantWatched}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            isRequired
            label="Шлюз"
            onChange={() => {
              resetField('currency', { defaultValue: null });
            }}
            optionText="name"
            optionValue="@id"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput
          enableGetChoices={() => !!gateway}
          filter={cleanEmpty({
            id: currencyIds.length ? currencyIds : undefined,
            active: true,
          })}
          perPage={50}
          reference="currencies"
          source="currency"
        >
          <AutocompleteInput
            disabled={!gatewayWatched}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            isRequired
            label="Валюта"
            optionText={(record) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="@id"
            variant="outlined"
          />
        </ReferenceInput>
        <TextInput
          fullWidth
          helperText={false}
          label="Общий комментарий пресетов пополнения"
          multiline
          source="paymentComment"
          sx={{ gridColumn: '1 / -1' }}
          variant="outlined"
        />
      </GeneralInfoBoxStyled>

      <PresetsItemsSection
        direction={PAYMENT_DIRECTION_ID}
        name="paymentItems"
        title="Пресеты пополнения"
      />
      <PresetsItemsSection
        direction={PAYOUT_DIRECTION_ID}
        name="payoutItems"
        title="Пресеты вывода"
      />

      {hasNoPresets && (
        <Alert severity="warning" sx={{ mb: '16px' }}>
          Для создания добавьте хотя бы один пресет пополнения или вывода
        </Alert>
      )}
    </Box>
  );
};
